import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Input,
  Label,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormFeedback,
} from "reactstrap";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { registerAgent } from "store/auth/register/registerSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { countries } from "constants/data/countries";
const AgentRegister = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { error, loading } = useSelector((state) => state.Register);

  const dispatch = useDispatch();

  const handleNext = async () => {
    const valid = await validation.validateForm();
    if (Object.keys(valid).length === 0) {
      setStep(2); // passe à l'étape 2
    } else {
      validation.setTouched(
        Object.keys(valid).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );
      console.log("errors " + valid);
      validation.setErrors(valid);
    }
  };
  const handlePrevious = () => setStep(1);
  const { t } = useTranslation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      nom: "",
      prenom: "",
      dateNaissance: "",
      email: "",
      numTelephone: "",
      numeroAgent: "",
      pays: "Algeria",
      typeAgent: "Intermédiaire",
      carteEvent: null,
      username: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object().shape({
      ...(step === 1
        ? {
            nom: Yup.string().required(t("validation.nom")),
            prenom: Yup.string().required(t("validation.prenom")),
            dateNaissance: Yup.string().required(t("validation.dateNaissance")),
            email: Yup.string()
              .email(t("validation.email.invalid"))
              .required(t("validation.email.required")),
            numTelephone: Yup.string()
              .matches(/^[0-9]+$/, t("validation.numTelephone.invalid"))
              .required(t("validation.numTelephone.required")),

            pays: Yup.string().required(t("validation.pays")),
            typeAgent: Yup.string().required(t("validation.typeAgent")),
            numeroAgent: Yup.string().when("typeAgent", (typeAgent) =>
              typeAgent === "Officiel"
                ? Yup.string().required(t("validation.numeroAgent"))
                : Yup.string().notRequired()
            ),

            carteEvent: Yup.mixed().when("typeAgent", (typeAgent, schema) => {
              if (typeAgent === "Officiel") {
                return Yup.mixed()
                  .required(t("validation.carteEvent.required"))
                  .test(
                    "fileType",
                    t("validation.carteEvent.fileType"),
                    (value) =>
                      value
                        ? ["image/jpeg", "image/png", "image/gif"].includes(
                            value.type
                          )
                        : false
                  )
                  .test(
                    "fileSize",
                    t("validation.carteEvent.fileSize"),
                    (value) => (value ? value.size <= 2 * 1024 * 1024 : false)
                  );
              }

              return Yup.mixed().notRequired();
            }),
          }
        : {
            username: Yup.string().required(t("validation.username")),
            password: Yup.string()
              .min(6, t("validation.password.min"))
              .required(t("validation.password.required")),
            confirmPassword: Yup.string()
              .oneOf(
                [Yup.ref("password"), null],
                t("validation.confirmPassword.match")
              )
              .required(t("validation.confirmPassword.required")),
          }),
    }),
    onSubmit: (values) => {
      const formData = new FormData();

      // Champs texte renommés
      formData.append("firstName", values.nom);
      formData.append("lastName", values.prenom);
      // formData.append("dateNaissance", values.dateNaissance) // pas de mapping
      formData.append("email", values.email);
      formData.append("phone", values.numTelephone);
      formData.append("licenseNumber", values.numeroAgent);
      formData.append("country", values.pays);
      // formData.append("typeAgent", values.typeAgent) // si le backend le garde tel quel
      formData.append("userName", values.username);
      formData.append("password", values.password);
      formData.append("confirmPassword", values.confirmPassword);

      // Fichier carte d'agent renommé
      if (values.carteEvent) {
        formData.append("licenseDocument", values.carteEvent);
      }

      dispatch(registerAgent({ agent: formData, history: navigate }));
    },
  });

  return (
    <Form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      {step === 1 ? (
        <>
          {/* Informations personnelles */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-4 fw-bold fs-4">
                {t("Informations personnelles")}
              </h5>

              <Row>
                <Col md={6}>
                  <Label>{t("Nom")} *</Label>
                  <Input
                    name="nom"
                    type="text"
                    value={validation.values.nom || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(validation.touched.nom && validation.errors.nom)
                    }
                  />
                  <FormFeedback>{validation.errors.nom}</FormFeedback>
                </Col>

                <Col md={6}>
                  <Label>{t("Prénom")} *</Label>
                  <Input
                    name="prenom"
                    type="text"
                    value={validation.values.prenom || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(validation.touched.prenom && validation.errors.prenom)
                    }
                  />
                  <FormFeedback>{validation.errors.prenom}</FormFeedback>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <Label>{t("Date de naissance")} *</Label>
                  <Input
                    name="dateNaissance"
                    type="date"
                    value={validation.values.dateNaissance || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.dateNaissance &&
                        validation.errors.dateNaissance
                      )
                    }
                  />
                  <FormFeedback>{validation.errors.dateNaissance}</FormFeedback>
                </Col>

                <Col md={6}>
                  <Label>{t("Email")} *</Label>
                  <Input
                    autocomplete="new-email"
                    name="email"
                    value={validation.values.email || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(validation.touched.email && validation.errors.email)
                    }
                  />
                  <FormFeedback>{validation.errors.email}</FormFeedback>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <Label>{t("Numéro de téléphone")} *</Label>
                  <Input
                    autoComplete="new-phone"
                    name="numTelephone"
                    value={validation.values.numTelephone || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.numTelephone &&
                        validation.errors.numTelephone
                      )
                    }
                  />
                  <FormFeedback>{validation.errors.numTelephone}</FormFeedback>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Informations professionnelles */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-4 fw-bold fs-4">
                {t("Informations professionnelles")}
              </h5>

              {/* Ligne 1 : Type + Pays */}
              <Row className="mb-3">
                <Col md={6}>
                  <Label>{t("Type d'agent")} *</Label>
                  <Input
                    type="select"
                    name="typeAgent"
                    value={validation.values.typeAgent || ""}
                    onChange={(e) => {
                      validation.handleChange(e);
                      validation.setFieldValue("typeAgent", e.target.value); // utile si vous utilisez Formik
                    }}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.typeAgent &&
                        validation.errors.typeAgent
                      )
                    }
                  >
                    <option value="Officiel">Officiel</option>
                    <option value="Intermédiaire">Intermédiaire</option>
                  </Input>
                  <FormFeedback>{validation.errors.typeAgent}</FormFeedback>
                </Col>

                <Col md={6}>
                  <Label>{t("Pays")} *</Label>
                  <Input
                    type="select"
                    name="pays"
                    value={validation.values.pays || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(validation.touched.pays && validation.errors.pays)
                    }
                  >
                    {countries.map((country) => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{validation.errors.pays}</FormFeedback>
                </Col>
              </Row>

              {/* Ligne 2 : si Officiel => afficher Numéro Agent + Carte Event */}
              {validation.values.typeAgent === "Officiel" && (
                <Row className="mb-3">
                  <Col md={6}>
                    <Label>{t("Numéro d'agent")} *</Label>
                    <Input
                      name="numeroAgent"
                      type="text"
                      value={validation.values.numeroAgent || ""}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(
                          validation.touched.numeroAgent &&
                          validation.errors.numeroAgent
                        )
                      }
                    />
                    <FormFeedback>{validation.errors.numeroAgent}</FormFeedback>
                  </Col>

                  <Col md={6}>
                    <Label>{t("Carte d'évènement officiel")} *</Label>
                    <Input
                      name="carteEvent"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          validation.setFieldValue("carteEvent", file);
                          validation.validateField("carteEvent");
                        }
                      }}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(
                          validation.touched.carteEvent &&
                          validation.errors.carteEvent
                        )
                      }
                    />
                    <FormFeedback>{validation.errors.carteEvent}</FormFeedback>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>

          <div className="text-end">
            <Button
              color="primary"
              type="button"
              onClick={() => {
                handleNext();
              }}
            >
              {t("Suivant")}
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Étape 2 : Création compte */}
          <Card className="mb-4">
            <CardBody>
              <h5 className="mb-4 fw-bold fs-4">{t("Création du compte")}</h5>

              {/* Ligne 1 - Nom d'utilisateur */}
              <Row className="mb-3">
                <Col md={12}>
                  <Label>{t("Nom d'utilisateur")} *</Label>
                  <Input
                    name="username"
                    autoComplete="off"
                    value={validation.values.username || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.username &&
                        validation.errors.username
                      )
                    }
                  />
                  <FormFeedback>{validation.errors.username}</FormFeedback>
                </Col>
              </Row>

              {/* Ligne 2 - Mot de passe */}
              <Row className="mb-3">
                <Col md={12}>
                  <Label>{t("Mot de passe")} *</Label>
                  <Input
                    autoComplete="new-password"
                    name="password"
                    type="password"
                    value={validation.values.password || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.password &&
                        validation.errors.password
                      )
                    }
                  />
                  <FormFeedback>{validation.errors.password}</FormFeedback>
                </Col>
              </Row>

              {/* Ligne 3 - Confirmer mot de passe */}
              <Row className="mb-3">
                <Col md={12}>
                  <Label>{t("Confirmer le mot de passe")} *</Label>
                  <Input
                    autoComplete="new-confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={validation.values.confirmPassword || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.confirmPassword &&
                        validation.errors.confirmPassword
                      )
                    }
                  />
                  <FormFeedback>
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                </Col>
              </Row>

              {/* Boutons */}
              <div className="mt-4 d-flex justify-content-between">
                <Button color="secondary" onClick={handlePrevious}>
                  {t("Précédent")}
                </Button>
                <Button color="primary" type="submit">
                  {t("register.signUp")}
                </Button>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </Form>
  );
};

export default AgentRegister;
